/**
 * Tooltip — DESIGN.md §13.6 ("nothing is hover-only").
 *
 * Used by the collapsed sidebar rail, where the icon is the only thing on
 * screen and the label has to come from somewhere. Two rules matter and are
 * baked in here rather than left to each call site: the tooltip opens on
 * keyboard focus as well as hover, and it is never the *only* way to learn
 * what a control does — the rail's buttons keep their `aria-label` too.
 */
import { forwardRef } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

/** 300ms: long enough not to flicker while the pointer crosses the rail. */
export const TOOLTIP_DELAY = 300;

export function TooltipProvider({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <TooltipPrimitive.Provider delayDuration={TOOLTIP_DELAY} skipDelayDuration={300}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 8, side = 'right', ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={sideOffset}
      className={cn(
        'bg-popover text-popover-foreground z-50 flex max-w-xs items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs shadow-md',
        'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** The keycap shown next to a tooltip label, e.g. the sidebar toggle's `[`. */
export function TooltipKeys({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="bg-muted text-muted-foreground rounded border px-1.5 py-0.5 font-mono text-[0.75rem] leading-none">
      {children}
    </kbd>
  );
}
